import { TRPCErrorBoundary } from '@client/components/_core/error-handling/TRPCErrorBoundary';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useUser } from '@client/hooks/business/user/useUser';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { getRoleDisplayName } from '@client/utils/roles/role-config';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { withRequiredRoles } from '@shared/constants/business/role-metadata';
import { SUPPORT_SESSION_STATUS_LABELS, type SupportSessionStatus } from '@shared/constants/business/support-session';
import type { OrgRole, Role } from '@shared/constants/business/user';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Empty, Form, Input, Modal, Space, Spin, Tabs, Tag, Tooltip, Typography } from 'antd';
import { Headphones } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';

const { Text, Paragraph } = Typography;

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/support-sessions')({
    staticData: {
        breadcrumb: { title: 'Support access' },
        ui: {
            icon: Headphones,
            title: 'Support access',
            subtitle: 'Review and manage support session requests',
            menuDescription: 'Review and manage support access requests',
        },
        permissions: ['support_session:read'],
    },
    component: SupportSessionsPage,
});

// Type aliases for the API responses
type PendingSession = RouterOutputs['supportSession']['listPending'][number];
type ActiveSession = RouterOutputs['supportSession']['listActive'][number];
type HistorySession = RouterOutputs['supportSession']['listHistory']['rows'][number];

function getStatusColor(status: SupportSessionStatus): 'blue' | 'green' | 'red' | 'default' | 'orange' {
    switch (status) {
        case 'pending':
            return 'blue';
        case 'approved':
            return 'green';
        case 'rejected':
            return 'red';
        case 'expired':
            return 'default';
        case 'revoked':
            return 'orange';
        default:
            return 'default';
    }
}

function formatDateTime(date: Date): string {
    return DateTime.fromJSDate(date).toFormat('dd/MM/yyyy HH:mm');
}

function SupportSessionsPage() {
    const { trpc, trpcUtils } = useTrpc();
    const user = useUser();
    const canApprove = hasPermission(user.roles, 'support_session:approve');

    // Fetch data for pending and active tabs
    const {
        data: pendingData,
        isLoading: isLoadingPending,
        error: pendingError,
    } = trpc.supportSession.listPending.useQuery();
    const {
        data: activeData,
        isLoading: isLoadingActive,
        error: activeError,
    } = trpc.supportSession.listActive.useQuery();

    const pendingCount = pendingData?.length ?? 0;

    return (
        <PageController route={Route}>
            <TRPCErrorBoundary error={pendingError || activeError}>
                <Tabs
                    defaultActiveKey="pending"
                    items={[
                        {
                            key: 'pending',
                            label: (
                                <span>
                                    Pending Requests
                                    {pendingCount > 0 && (
                                        <Tag color="blue" className="ml-2">
                                            {pendingCount}
                                        </Tag>
                                    )}
                                </span>
                            ),
                            children: (
                                <PendingRequestsTab
                                    sessions={pendingData ?? []}
                                    isLoading={isLoadingPending}
                                    canApprove={canApprove}
                                    onAction={() => {
                                        void trpcUtils.supportSession.listPending.invalidate();
                                        void trpcUtils.supportSession.listActive.invalidate();
                                        void trpcUtils.supportSession.listHistory.invalidate();
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'active',
                            label: 'Active Sessions',
                            children: (
                                <ActiveSessionsTab
                                    sessions={activeData ?? []}
                                    isLoading={isLoadingActive}
                                    canApprove={canApprove}
                                    onAction={() => {
                                        void trpcUtils.supportSession.listActive.invalidate();
                                        void trpcUtils.supportSession.listHistory.invalidate();
                                    }}
                                />
                            ),
                        },
                        {
                            key: 'history',
                            label: 'History',
                            children: <HistoryTab />,
                        },
                    ]}
                />
            </TRPCErrorBoundary>
        </PageController>
    );
}

function getDisplayName(user: { firstName: string | null; lastName: string | null; email: string }) {
    return user.firstName || user.lastName ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : null;
}

interface PendingRequestsTabProps {
    sessions: PendingSession[];
    isLoading: boolean;
    canApprove: boolean;
    onAction: () => void;
}

function PendingRequestsTab({ sessions, isLoading, canApprove, onAction }: PendingRequestsTabProps) {
    const { trpcClient } = useTrpc();
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | null>(null);
    const [selectedSession, setSelectedSession] = useState<PendingSession | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    const handleOpenReviewModal = (session: PendingSession, action: 'approve' | 'reject') => {
        setSelectedSession(session);
        setReviewAction(action);
        setReviewModalOpen(true);
        form.resetFields();
    };

    const handleSubmitReview = async (values: { reviewNote?: string }) => {
        if (!selectedSession || !reviewAction) return;

        setIsSubmitting(true);
        try {
            if (reviewAction === 'approve') {
                await trpcClient.supportSession.approve.mutate({
                    id: selectedSession.id,
                    reviewNote: values.reviewNote,
                });
                message.success('Support session approved successfully');
            } else {
                await trpcClient.supportSession.reject.mutate({
                    id: selectedSession.id,
                    reviewNote: values.reviewNote,
                });
                message.success('Support session rejected');
            }
            setReviewModalOpen(false);
            onAction();
        } catch (err) {
            errorMessage.show(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Spin size="large" />
            </div>
        );
    }

    if (sessions.length === 0) {
        return <Empty description="No pending support session requests" className="py-12" />;
    }

    return (
        <>
            <div className="space-y-4">
                {sessions.map((session) => {
                    const supportUserName = getDisplayName(session.SupportUser);
                    const requestedByName = getDisplayName(session.RequestedByUser);

                    return (
                        <Card key={String(session.id)} size="small">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="mb-3 flex items-center gap-2">
                                        {supportUserName ? (
                                            <>
                                                <Text strong className="text-base">
                                                    {supportUserName}
                                                </Text>
                                                <Text type="secondary">{session.SupportUser.email}</Text>
                                            </>
                                        ) : (
                                            <Text strong className="text-base">
                                                {session.SupportUser.email}
                                            </Text>
                                        )}
                                        <Tag variant="outlined" color="blue">
                                            Pending
                                        </Tag>
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                        <div>
                                            <Text type="secondary">Requested Roles: </Text>
                                            <span className="inline-flex flex-wrap gap-1">
                                                {/* Show what approval will actually grant — legacy requests
                                                    may predate request-time pairing (CUR-851). */}
                                                {withRequiredRoles(session.requestedRoles as OrgRole[]).map((role) => (
                                                    <Tag key={role} variant="outlined" color="blue">
                                                        {getRoleDisplayName(role)}
                                                    </Tag>
                                                ))}
                                            </span>
                                        </div>
                                        <div>
                                            <Text type="secondary">Expires: </Text>
                                            <Text>{formatDateTime(session.expiresAt)}</Text>
                                        </div>
                                        <div>
                                            <Text type="secondary">Requested By: </Text>
                                            <Tooltip title={session.RequestedByUser.email}>
                                                <Text>{requestedByName || session.RequestedByUser.email}</Text>
                                            </Tooltip>
                                        </div>
                                        <div>
                                            <Text type="secondary">Requested At: </Text>
                                            <Text>{formatDateTime(session.createdAt)}</Text>
                                        </div>
                                    </div>

                                    {session.requestNote && (
                                        <div className="mt-3 rounded-md bg-muted p-3">
                                            <Text type="secondary" className="mb-1 block text-xs">
                                                Request Note:
                                            </Text>
                                            <Paragraph className="mb-0 whitespace-pre-wrap text-sm">
                                                {session.requestNote}
                                            </Paragraph>
                                        </div>
                                    )}
                                </div>

                                {canApprove && (
                                    <Space className="ml-4">
                                        <Button
                                            type="primary"
                                            onClick={() => handleOpenReviewModal(session, 'approve')}
                                        >
                                            Approve
                                        </Button>
                                        <Button type="danger" onClick={() => handleOpenReviewModal(session, 'reject')}>
                                            Reject
                                        </Button>
                                    </Space>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Modal
                title={reviewAction === 'approve' ? 'Approve Support Session' : 'Reject Support Session'}
                open={reviewModalOpen}
                onCancel={() => setReviewModalOpen(false)}
                footer={null}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={handleSubmitReview}>
                    {selectedSession && (
                        <div className="mb-4 rounded-md bg-muted p-3">
                            <Text strong>
                                {getDisplayName(selectedSession.SupportUser) || selectedSession.SupportUser.email}
                            </Text>
                            <br />
                            <Text type="secondary">{selectedSession.SupportUser.email}</Text>
                        </div>
                    )}

                    <Form.Item
                        name="reviewNote"
                        label={reviewAction === 'approve' ? 'Note (optional)' : 'Reason for rejection (optional)'}
                    >
                        <Input.TextArea
                            placeholder={
                                reviewAction === 'approve'
                                    ? 'Add an optional note...'
                                    : 'Provide a reason for rejecting this request...'
                            }
                            rows={3}
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <div className="flex justify-end gap-2">
                        <Button type="secondary" onClick={() => setReviewModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            type={reviewAction === 'reject' ? 'danger-filled' : 'primary'}
                            htmlType="submit"
                            loading={isSubmitting}
                        >
                            {reviewAction === 'approve' ? 'Approve' : 'Reject'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}

interface ActiveSessionsTabProps {
    sessions: ActiveSession[];
    isLoading: boolean;
    canApprove: boolean;
    onAction: () => void;
}

function ActiveSessionsTab({ sessions, isLoading, canApprove, onAction }: ActiveSessionsTabProps) {
    const { trpcClient } = useTrpc();
    const [revokeModalOpen, setRevokeModalOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState<ActiveSession | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();

    const handleOpenRevokeModal = (session: ActiveSession) => {
        setSelectedSession(session);
        setRevokeModalOpen(true);
        form.resetFields();
    };

    const handleSubmitRevoke = async (values: { revokeReason?: string }) => {
        if (!selectedSession) return;

        setIsSubmitting(true);
        try {
            await trpcClient.supportSession.revoke.mutate({
                id: selectedSession.id,
                revokeReason: values.revokeReason,
            });
            message.success('Support session revoked successfully');
            setRevokeModalOpen(false);
            onAction();
        } catch (err) {
            errorMessage.show(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Spin size="large" />
            </div>
        );
    }

    if (sessions.length === 0) {
        return <Empty description="No active support sessions" className="py-12" />;
    }

    return (
        <>
            <div className="space-y-4">
                {sessions.map((session) => {
                    const isExpiringSoon = DateTime.fromJSDate(session.expiresAt).diffNow('days').days < 1;
                    const supportUserName = getDisplayName(session.SupportUser);
                    const reviewerName = session.ReviewedByUser?.User
                        ? getDisplayName(session.ReviewedByUser.User)
                        : null;
                    const isAutoApproved = !session.ReviewedByUser?.User && session.reviewedAt;

                    return (
                        <Card key={String(session.id)} size="small">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="mb-3 flex items-center gap-2">
                                        {supportUserName ? (
                                            <>
                                                <Text strong className="text-base">
                                                    {supportUserName}
                                                </Text>
                                                <Text type="secondary">{session.SupportUser.email}</Text>
                                            </>
                                        ) : (
                                            <Text strong className="text-base">
                                                {session.SupportUser.email}
                                            </Text>
                                        )}
                                        <Tag variant="outlined" color="green">
                                            Active
                                        </Tag>
                                    </div>

                                    <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                        <div>
                                            <Text type="secondary">Roles: </Text>
                                            <span className="inline-flex flex-wrap gap-1">
                                                {/* Show what the membership actually holds — approval
                                                    appends mandatory companion roles (CUR-846). */}
                                                {withRequiredRoles(session.requestedRoles as OrgRole[]).map((role) => (
                                                    <Tag key={role} variant="outlined" color="blue">
                                                        {getRoleDisplayName(role)}
                                                    </Tag>
                                                ))}
                                            </span>
                                        </div>
                                        <div>
                                            <Text type="secondary">Expires: </Text>
                                            <span className={isExpiringSoon ? 'text-orange-500' : ''}>
                                                {formatDateTime(session.expiresAt)}
                                                {isExpiringSoon && (
                                                    <Tooltip title="Expires within 24 hours">
                                                        <Tag color="orange" className="ml-2">
                                                            Soon
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                            </span>
                                        </div>
                                        <div>
                                            <Text type="secondary">Approved By: </Text>
                                            {isAutoApproved ? (
                                                <Tooltip title="Automatically approved during organisation setup">
                                                    <Text italic>System (Auto-approved)</Text>
                                                </Tooltip>
                                            ) : reviewerName ? (
                                                <Text>{reviewerName}</Text>
                                            ) : (
                                                <Text>-</Text>
                                            )}
                                        </div>
                                        <div>
                                            <Text type="secondary">Approved At: </Text>
                                            <Text>{session.reviewedAt ? formatDateTime(session.reviewedAt) : '-'}</Text>
                                        </div>
                                    </div>
                                </div>

                                {canApprove && (
                                    <Button
                                        type="danger"
                                        className="ml-4"
                                        onClick={() => handleOpenRevokeModal(session)}
                                    >
                                        Revoke Access
                                    </Button>
                                )}
                            </div>
                        </Card>
                    );
                })}
            </div>

            <Modal
                title="Revoke Support Session"
                open={revokeModalOpen}
                onCancel={() => setRevokeModalOpen(false)}
                footer={null}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={handleSubmitRevoke}>
                    {selectedSession && (
                        <div className="mb-4 rounded-md bg-muted p-3">
                            <Text strong>
                                {getDisplayName(selectedSession.SupportUser) || selectedSession.SupportUser.email}
                            </Text>
                            <br />
                            <Text type="secondary">{selectedSession.SupportUser.email}</Text>
                        </div>
                    )}

                    <div className="mb-4 rounded-md border border-orange-200 bg-orange-50 p-3">
                        <Text type="secondary" className="text-xs">
                            <strong>Warning:</strong> Revoking this session will immediately remove the support
                            account's access to your organisation. This action cannot be undone.
                        </Text>
                    </div>

                    <Form.Item name="revokeReason" label="Reason for revocation (optional)">
                        <Input.TextArea
                            placeholder="Provide a reason for revoking this session..."
                            rows={3}
                            maxLength={1000}
                            showCount
                        />
                    </Form.Item>

                    <div className="flex justify-end gap-2">
                        <Button type="secondary" onClick={() => setRevokeModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="danger-filled" htmlType="submit" loading={isSubmitting}>
                            Revoke Access
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}

type HistoryQueryVariables = RouterInputs['supportSession']['listHistory'];
type HistoryOrderBy = NonNullable<HistoryQueryVariables['orderBy']>;

const historyColumnHelper = createColumnHelper<HistorySession, HistoryOrderBy>();

function HistoryTab() {
    const { trpc } = useTrpc();

    const [queryVariables, setQueryVariables] = usePathState<HistoryQueryVariables>('historyQuery', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { createdAt: 'desc' },
    });

    const { data, refetch, isFetching, error } = trpc.supportSession.listHistory.useQuery(queryVariables);

    const columns = [
        historyColumnHelper.accessor('SupportUser', {
            id: 'supportUser',
            header: 'Support Account',
            cell: ({ value }) => {
                const name = getDisplayName(value);
                return name ? (
                    <div>
                        <Text strong>{name}</Text>
                        <br />
                        <Text type="secondary" className="text-xs">
                            {value.email}
                        </Text>
                    </div>
                ) : (
                    <Text strong>{value.email}</Text>
                );
            },
        }),
        historyColumnHelper.accessor('requestedRoles', {
            id: 'roles',
            header: 'Roles',
            cell: ({ value }) => (
                <div className="flex flex-wrap gap-1">
                    {(value as Role[]).slice(0, 2).map((role) => (
                        <Tag key={role} variant="outlined" color="blue">
                            {getRoleDisplayName(role)}
                        </Tag>
                    ))}
                    {(value as Role[]).length > 2 && (
                        <Tooltip title={(value as Role[]).slice(2).map(getRoleDisplayName).join(', ')}>
                            <Tag variant="outlined">+{(value as Role[]).length - 2}</Tag>
                        </Tooltip>
                    )}
                </div>
            ),
        }),
        historyColumnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ value }) => (
                <Tag variant="outlined" color={getStatusColor(value as SupportSessionStatus)}>
                    {SUPPORT_SESSION_STATUS_LABELS[value as SupportSessionStatus]}
                </Tag>
            ),
            width: 120,
        }),
        historyColumnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Created',
            sortable: 'createdAt',
            cell: ({ value }) => formatDateTime(value),
        }),
        historyColumnHelper.accessor('expiresAt', {
            id: 'expiresAt',
            header: 'Expires',
            sortable: 'expiresAt',
            cell: ({ value }) => formatDateTime(value),
        }),
        historyColumnHelper.accessor('RequestedByUser', {
            id: 'requestedBy',
            header: 'Requested By',
            cell: ({ value }) => {
                const name = getDisplayName(value);
                return <Tooltip title={value.email}>{name || value.email}</Tooltip>;
            },
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'org-support-sessions-history-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    return (
        <TRPCErrorBoundary error={error}>
            <PrismaTable {...tableProps} />
        </TRPCErrorBoundary>
    );
}
